import { useEffect, useMemo } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { MoneyInput } from '@/components/ui/money-input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { enabledCurrencyList } from '@/lib/currency'
import { NET_WORTH_ASSET_TYPES, NET_WORTH_LIABILITY_TYPES, netWorthType } from '@/lib/networth'
import { useHousehold } from '@/context/HouseholdContext'
import { useSaveNetWorthItem, type NetWorthItemInput } from '@/hooks/useNetWorth'
import { useT, type TFn } from '@/i18n/I18nProvider'
import type { NetWorthItem, NetWorthKind } from '@/types/db'

function makeSchema(t: TFn) {
  return z.object({
    name: z.string().min(1, t('rep.valName')),
    item_type: z.string().min(1, t('rep.valType')),
    value: z.number({ message: t('rep.valValueInvalid') }).min(0, t('rep.valValueNegative')),
    currency: z.string().min(3),
    notes: z.string().optional(),
  })
}
type FormValues = z.infer<ReturnType<typeof makeSchema>>

export function NetWorthItemDialog({
  open,
  onOpenChange,
  item,
  defaultKind = 'asset',
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  item?: NetWorthItem | null
  defaultKind?: NetWorthKind
}) {
  const { t } = useT()
  const { baseCurrency, enabledCurrencies } = useHousehold()
  const save = useSaveNetWorthItem()
  const schema = useMemo(() => makeSchema(t), [t])

  const defaultType =
    item?.item_type ?? (defaultKind === 'asset' ? NET_WORTH_ASSET_TYPES[0].value : NET_WORTH_LIABILITY_TYPES[0].value)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      name: item?.name ?? '',
      item_type: defaultType,
      value: item?.value ?? 0,
      currency: item?.currency ?? baseCurrency,
      notes: item?.notes ?? '',
    },
  })

  // Descarta edições abandonadas: ao reabrir, reset() re-aplica os `values` atuais.
  useEffect(() => {
    if (open) reset()
  }, [open, reset])

  const selectedType = watch('item_type')
  const typeInfo = netWorthType(selectedType)

  async function onSubmit(values: FormValues) {
    const info = netWorthType(values.item_type)
    const payload: NetWorthItemInput = {
      name: values.name,
      kind: info.kind, // derivado do tipo
      item_type: values.item_type,
      value: values.value,
      currency: values.currency,
      group_name: info.label,
      notes: values.notes?.trim() || null,
    }
    try {
      await save.mutateAsync({ id: item?.id, values: payload })
      toast.success(item ? t('rep.dlgItemUpdated') : t('rep.dlgItemAdded'))
      reset()
      onOpenChange(false)
    } catch (e) {
      toast.error(t('rep.dlgSaveError'), { description: (e as Error).message })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>{item ? t('rep.dlgEditTitle') : t('rep.dlgNewTitle')}</DialogTitle>
            <DialogDescription>{t('rep.dlgDesc')}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>{t('rep.dlgTypeLabel')}</Label>
              <Select value={selectedType} onValueChange={(v) => setValue('item_type', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>{t('rep.dlgAssetsGroup')}</SelectLabel>
                    {NET_WORTH_ASSET_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>{t('rep.dlgLiabilitiesGroup')}</SelectLabel>
                    {NET_WORTH_LIABILITY_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
                <typeInfo.icon className={`size-3.5 ${typeInfo.color}`} />
                {typeInfo.kind === 'asset' ? t('rep.dlgAsset') : t('rep.dlgLiability')}{' '}
                {t('rep.dlgExamples', { examples: typeInfo.examples })}
              </p>
              {errors.item_type && <p className="text-destructive text-xs">{errors.item_type.message}</p>}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="name">{t('rep.dlgNameLabel')}</Label>
              <Input id="name" {...register('name')} placeholder={t('rep.dlgNamePlaceholder')} />
              {errors.name && <p className="text-destructive text-xs">{errors.name.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{t('rep.dlgValueLabel')}</Label>
                <Controller
                  control={control}
                  name="value"
                  render={({ field }) => (
                    <MoneyInput value={field.value} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} />
                  )}
                />
                {errors.value && <p className="text-destructive text-xs">{errors.value.message}</p>}
              </div>
              <div className="grid gap-2">
                <Label>{t('rep.dlgCurrencyLabel')}</Label>
                <Select value={watch('currency')} onValueChange={(v) => setValue('currency', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {enabledCurrencyList(enabledCurrencies).map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.code} — {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>{t('rep.dlgNotesLabel')}</Label>
              <Textarea rows={2} {...register('notes')} placeholder={t('rep.dlgNotesPlaceholder')} />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
